import { useEffect, useRef, useState } from 'react'
import { apiClient } from '@/api/client'
import type { PrintJob, StationKOT } from '@/types'
import { desktop, desktopPrintAsync } from '@/lib/printTransport'
import {
  getPrintAgentConfig,
  getReceiptPrinter,
  getStationPrinterConfig,
  printAgentClaimLabel,
  subscribePrintAgentConfig,
} from '@/lib/stationPrinterMap'
import { buildPrintHtml } from '@/lib/printKotReceipt'
import { printDebug } from '@/lib/printDebug'

/**
 * The counter print agent (F-FE-N60).
 *
 * A waiter's tablet cannot reach the thermal printer hanging off the counter PC
 * by USB — the only non-desktop print path opens a raw socket to ip:9100, and a
 * USB printer has no ip:port. So the backend queues those slips and this, the
 * desktop app already sitting on the counter, claims them and prints them.
 *
 * Why polling and not SSE: internal/realtime is a deliberately lossy hub — it
 * drops events for slow subscribers because its payloads are cache-busters. A
 * dropped cache-bust costs a stale tile; a dropped print instruction costs a
 * dish nobody cooks. The DB row is the contract, and a poll cannot lose one.
 */

/** How often to ask for work. A waiter walking from the table takes longer. */
const POLL_MS = 3000

/** Back off to this when the backend is unreachable, so an offline till isn't hammering. */
const POLL_MS_OFFLINE = 15000

export interface PrintAgentState {
  /** The agent is switched on for this machine and running. */
  active: boolean
  /** Slips printed since this page loaded — a cheap "it's working" signal. */
  printed: number
  /** Slips that came back failed. */
  failed: number
  lastError: string | null
}

/**
 * Turn one queued job into the same HTML the counter builds for its own slips.
 *
 * Reusing buildPrintHtml is the point: a relayed slip and a locally-printed one
 * are then byte-identical, so nothing about the paper depends on which machine
 * fired it. Exported for tests.
 */
export function printJobToStationKOT(job: PrintJob): StationKOT {
  return {
    station_id: job.station_id ?? '',
    station_name: job.station_name,
    output_type: 'printer',
    print_location: 'counter',
    printer_target: job.printer_target,
    payload: job.payload_text,
    slip: job.slip ?? undefined,
  }
}

/**
 * Which physical printer a relayed slip goes to on THIS machine.
 *
 * Same precedence the counter already uses for its own slips
 * (printKotReceipts): the machine-local station map first, then the receipt
 * printer, then the OS default. So a tenant that has already mapped its
 * stations on this PC needs no new configuration at all.
 *
 * Note the backend's printer_target is NOT consulted. For a relayed job it is
 * an ip:port meant for the Android agent, and handing that to SumatraPDF as a
 * Windows printer name would fail every job. Exported for tests.
 */
export function resolveRelayDevice(
  stationId: string,
  opts: { stationDevice?: string; receiptDevice?: string } = {},
): string | undefined {
  const station = opts.stationDevice ?? getStationPrinterConfig(stationId).device
  if (station) return station
  const receipt = opts.receiptDevice ?? getReceiptPrinter()
  if (receipt) return receipt
  return undefined
}

interface ReceiptWidths {
  paperWidthMm: number
  printableAreaMm: number
  kotFontScale: number
}

/**
 * Print one job and report the outcome. Split out so the loop below stays
 * readable and so a single job's failure can never abort the batch.
 */
async function printOneJob(job: PrintJob, cfg: ReceiptWidths): Promise<{ ok: boolean; error?: string }> {
  const stationCfg = getStationPrinterConfig(job.station_id ?? '')
  const widthMm = stationCfg.paperWidthMm && stationCfg.paperWidthMm > 0 ? stationCfg.paperWidthMm : cfg.paperWidthMm
  const printableMm = Math.min(cfg.printableAreaMm, Math.max(40, widthMm - 6))

  const html = buildPrintHtml([printJobToStationKOT(job)], {
    paperWidthMm: widthMm,
    printableAreaMm: printableMm,
    fontScale: cfg.kotFontScale,
  })

  const result = await desktopPrintAsync(html, {
    deviceName: resolveRelayDevice(job.station_id ?? ''),
    paperWidthMm: widthMm,
    printableAreaMm: printableMm,
  })

  // null means the desktop bridge vanished mid-flight (the agent should not have
  // been running at all). Report it as a failure rather than silently acking OK
  // — the job returns to pending and a real terminal can take it.
  if (!result) return { ok: false, error: 'desktop bridge unavailable' }
  return { ok: result.ok, error: result.error }
}

/**
 * Claim-print-ack, once. Exported so a test can drive a single cycle without
 * waiting on timers. Returns how many jobs were handled.
 */
export async function runPrintAgentCycle(cfg: ReceiptWidths, terminalLabel: string): Promise<{
  printed: number
  failed: number
}> {
  let printed = 0
  let failed = 0

  const claim = await apiClient.claimPrintJobs(terminalLabel)
  const jobs = claim.data?.jobs ?? []
  if (jobs.length === 0) return { printed, failed }

  printDebug.log('agent', `claimed ${jobs.length} relayed slip(s)`)

  // Serial, not parallel: each job spawns SumatraPDF, and the slips should come
  // out of the printer in the order they were fired rather than interleaved.
  for (const job of jobs) {
    let outcome: { ok: boolean; error?: string }
    try {
      outcome = await printOneJob(job, cfg)
    } catch (e) {
      outcome = { ok: false, error: e instanceof Error ? e.message : String(e) }
    }
    if (outcome.ok) printed += 1
    else failed += 1

    // Ack even on failure — that is what lets the backend retry it, and what
    // stops it sitting 'claimed' until the 90s timeout re-offers it. The label
    // is the one we claimed with: the backend ignores an ack from a terminal
    // that no longer holds the claim (F-BE-N79).
    try {
      await apiClient.ackPrintJob(job.id, outcome.ok, outcome.error, terminalLabel)
    } catch (e) {
      printDebug.warn('agent', 'ack failed; job will be re-offered after the claim timeout', e)
    }
  }

  return { printed, failed }
}

/**
 * Run the agent for as long as this app is open.
 *
 * Mount it in the admin shell, not on the counter screen: the counter PC has to
 * keep printing while the cashier is on Reports or Day Close, and a waiter has
 * no way of knowing which screen the till happens to be showing.
 */
export function useCounterPrintAgent(cfg: ReceiptWidths): PrintAgentState {
  const [state, setState] = useState<PrintAgentState>({
    active: false,
    printed: 0,
    failed: 0,
    lastError: null,
  })

  // Keep the widths current without restarting the loop on every settings read.
  const cfgRef = useRef(cfg)
  cfgRef.current = cfg

  // The enable flag has to be REACTIVE, not read once at mount. Reading it once
  // meant an operator could switch the agent on, see the tile go active, and
  // still have nothing claimed until the app was restarted — while tablets
  // queued paper that never came out. Silent, and indistinguishable from a
  // broken relay.
  const [agent, setAgent] = useState(() => getPrintAgentConfig())
  useEffect(() => subscribePrintAgentConfig(() => setAgent(getPrintAgentConfig())), [])

  const enabled = agent.enabled
  const label = printAgentClaimLabel(agent)

  useEffect(() => {
    if (!desktop() || !enabled) {
      setState((s) => (s.active ? { ...s, active: false } : s))
      return
    }

    let stopped = false
    let timer: number | null = null
    // Guards against a slow print cycle overlapping the next tick, which would
    // claim a second batch while the first is still feeding the printer.
    let running = false

    setState((s) => ({ ...s, active: true }))
    printDebug.log('agent', `counter print agent started as "${label}"`)

    const tick = async () => {
      if (stopped || running) return
      running = true
      let delay = POLL_MS
      try {
        const { printed, failed } = await runPrintAgentCycle(cfgRef.current, label)
        if (!stopped && (printed || failed)) {
          setState((s) => ({
            ...s,
            printed: s.printed + printed,
            failed: s.failed + failed,
            lastError: failed ? s.lastError : null,
          }))
        }
      } catch (e) {
        // Almost always the backend being unreachable. Back off rather than
        // retrying every 3s from a till that has lost its connection.
        delay = POLL_MS_OFFLINE
        const msg = e instanceof Error ? e.message : String(e)
        if (!stopped) setState((s) => (s.lastError === msg ? s : { ...s, lastError: msg }))
      } finally {
        running = false
        if (!stopped) timer = window.setTimeout(tick, delay)
      }
    }

    // First pass immediately: reopening the app should flush the backlog that
    // built up while it was closed, not wait out a poll interval first.
    void tick()

    return () => {
      stopped = true
      if (timer !== null) window.clearTimeout(timer)
      printDebug.log('agent', 'counter print agent stopped')
    }
    // Restart only when the agent is switched on/off or the terminal is renamed.
    // The paper widths deliberately ride a ref instead: a settings refetch must
    // not tear down the loop between a claim and its ack, which would strand the
    // batch as 'claimed' until the 90s timeout re-offered it.
  }, [enabled, label])

  return state
}
